import { useState, useMemo } from 'react';
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import {
  numToCurrency,
  downloadExcel,
  downloadPdf,
  capitalizeFirstLetter,
  normalToCamel,
  convertAllDates,
  camelToTitle,
  sortNestedSuites,
} from '../../utils/Functions';
import { useSelector } from 'react-redux';
import { selectProject } from '../../features/project/projectSlice';
import StandardTable from '../tables/StandardTable';
import { Flex, FlexBetween } from '../../commonStyles';
import { GlobalModal } from '../../features/modal/Modal';
import { IDeal, IDealDeposit } from '../../types/CreateDealForm';
import { useAppDispatch } from '../../app/hooks';
import { handleModal } from '../../features/modal/modalSlice';
import { useNavigate } from 'react-router-dom';
import { showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';

const DailyDeposits = () => {
  const storeDispatch = useAppDispatch();
  const navigate = useNavigate();
  const project = useSelector(selectProject);
  const [deposits, setDeposits] = useState<any[]>([]);
  const [depositColumns, setDepositColumns] = useState<string[]>([]);
  const [suites, setSuites] = useState<IDeal[]>([]);
  const [depositType, setDepositType] = useState<string>('');
  const [title, setTitle] = useState<string>('');

  const { loading } = useQuery(DEPOSITS, {
    variables: { project: project._id },
    onCompleted: async (data) => {
      let unique: any[] = [...new Set(data.getDepositSummary.map((deposit: any) => deposit._id.deposit))];
      if (unique.includes('Initial')) {
        unique.sort(function (x, y) {
          return x === 'Initial' ? -1 : y === 'Initial' ? 1 : 0;
        });
      }

      let output: any[] = [];
      let deposits = [...data.getDepositSummary];

      await deposits.forEach(function (item: any) {
        var existing = output.filter(function (v, i) {
          return v._id.year == item._id.year && v._id.month == item._id.month;
        });
        if (existing.length) {
          var existingIndex = output.indexOf(existing[0]);
          output[existingIndex].total += item.total;
          output[existingIndex].deposits = [
            ...output[existingIndex].deposits,
            { name: item._id.deposit, amount: item.total, receivedTotal: item.receivedTotal },
          ];
        } else {
          let newItem = { ...item };
          newItem.deposits = [{ name: item._id.deposit, amount: item.total, receivedTotal: item.receivedTotal }];
          output.push(newItem);
        }
      });

      setDeposits(output);
      setDepositColumns(unique);
    },
  });

  const [getDepositUnits, { loading: suiteLoading }] = useLazyQuery(DEPOSITUNITS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      let units = data.getDepositUnits.map((deal: IDeal) => {
        let deposit = deal.deposit.find((deposit: IDealDeposit) => deposit.name === depositType);
        return {
          suite: deal.unit,
          chequeType: deposit ? camelToTitle(deposit.chequeType!) : '',
          amount: deposit ? numToCurrency.format(deposit.amount!) : '',
          dueDate: deposit ? convertAllDates(deposit.dueDate, 'PP') : '',
        };
      });
      setSuites(sortNestedSuites(units, 'suite', 'suite'));
    },
  });

  const [downloadDepositTracker, { loading: reportLoading }] = useLazyQuery(DEPOSITREPORT, {
    variables: { project: project._id },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('An email has been sent to you'))
    },
  });

  const columns = useMemo(() => {
    let date = [
      {
        Header: 'Date',
        accessor: (rowData: any) => {
          return (
            <strong>
              {rowData._id.month}/{rowData._id.year}
            </strong>
          );
        },
      },
    ];

    let depositCols = depositColumns.map((unique: string) => {
      return {
        Header: `${unique}`,
        accessor: (rowData: any) => {
          let selectedDeposit = rowData.deposits.find((deposit: any) => deposit.name === unique);
          if (selectedDeposit) {
            return (
              <Flex
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setDepositType(unique);
                  setTitle(`${unique} - ${rowData._id.month}/${rowData._id.year}`);
                  storeDispatch(handleModal(true));
                  getDepositUnits({
                    variables: { project: project._id, name: unique, date: new Date(`${rowData._id.year}-${rowData._id.month}-15`) },
                  });
                }}
              >
                <Box>{numToCurrency.format(selectedDeposit.amount)}</Box>
                {selectedDeposit.receivedTotal ? (
                  <Box sx={{ ml: 0.5, color: 'green' }}>({numToCurrency.format(selectedDeposit.receivedTotal)})</Box>
                ) : null}
              </Flex>
            );
          }
          return '-';
        },
      };
    });

    let total = [
      {
        Header: `Monthly Total`,
        accessor: (rowData: any) => {
          return (
            <Flex>
              <Box>{numToCurrency.format(rowData.total)}</Box>
              {rowData.deposits.length ? (
                <Box sx={{ ml: 0.5, color: 'green' }}>
                  (
                  {numToCurrency.format(
                    rowData.deposits.reduce((a: any, b: any) => {
                      return a + b.receivedTotal;
                    }, 0)
                  )}
                  )
                </Box>
              ) : null}
            </Flex>
          );
        },
      },
    ];
    let accumulative = [
      {
        Header: `Accumulative`,
        accessor: (rowData: any, index: number) => {
          let amount = 0;
          let receivedTotal = 0;
          for (let i = 0; i <= index; i++) {
            amount += deposits[i].total;
          }
          for (let i = 0; i <= index; i++) {
            receivedTotal += deposits[i].deposits.reduce((a: any, b: any) => {
              return a + b.receivedTotal;
            }, 0);
          }

          return (
            <Flex>
              <Box>{numToCurrency.format(amount)}</Box>
              {receivedTotal ? <Box sx={{ ml: 0.5, color: 'green' }}>({numToCurrency.format(receivedTotal)})</Box> : null}
            </Flex>
          );
        },
      },
    ];

    return [...date, ...depositCols, ...total, ...accumulative];
  }, [deposits]);

  const unitColumns = useMemo(() => {
    return [
      {
        Header: 'Suite',
        accessor: (rowData: any) => {
          return (
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                storeDispatch(handleModal(false));
                navigate(`/${project._id}/dashboard/${rowData.suite._id}`);
              }}
            >
              <strong>{rowData.suite.suite}</strong>
            </Box>
          );
        },
      },
      {
        Header: 'Cheque Type',
        accessor: (rowData: any) => rowData.chequeType,
      },
      {
        Header: 'Amount',
        accessor: (rowData: any) => rowData.amount,
      },
      {
        Header: 'Due Date',
        accessor: (rowData: any) => rowData.dueDate,
      },
    ];
  }, [suites]);

  const download = (type: string, data: any) => {
    let unitColumns = data.map((depositData: any, index: number) => {
      let amount = 0;
      let receivedTotal = 0;
      for (let i = 0; i <= index; i++) {
        amount += deposits[i].total;
        receivedTotal += deposits[i].deposits.reduce((a: any, b: any) => {
          return a + b.receivedTotal;
        }, 0);
      }
      let newDepositColumn = depositColumns.map((unique: string) => {
        let selectedDeposit = depositData.deposits.find((deposit: any) => deposit.name === unique);
        if (selectedDeposit) {
          return `${numToCurrency.format(selectedDeposit.amount)} (${numToCurrency.format(selectedDeposit.receivedTotal)})`;
        } else return '-';
      });

      let depositObjects = depositColumns.map((unique: string, index: number) => {
        return {
          [normalToCamel(unique)]: newDepositColumn[index],
        };
      });

      let mergeObjects = depositObjects.reduce(function (result, currentObject) {
        for (var key in currentObject) {
          if (currentObject.hasOwnProperty(key)) {
            result[key] = currentObject[key];
          }
        }
        return result;
      }, {});

      let allObjects = {
        date: `${depositData._id.month}/${depositData._id.year}`,
        monthlyTotal: numToCurrency.format(depositData.total),
        monthlyReceived: numToCurrency.format(
          depositData.deposits.reduce((a: any, b: any) => {
            return a + b.receivedTotal;
          }, 0)
        ),
        accumulative: numToCurrency.format(amount),
        accumulativeReceived: numToCurrency.format(receivedTotal),
      };

      return { ...allObjects, ...mergeObjects };
    });

    let headers = [
      {
        label: 'Date',
        id: 'date',
      },
    ];

    let depositHeaders = depositColumns.map((deposit: any) => {
      return {
        label: deposit,
        id: normalToCamel(deposit),
      };
    });

    let totalHeaders = [
      {
        label: 'Monthly Total',
        id: 'monthlyTotal',
      },
      {
        label: 'Monthly Received',
        id: 'monthlyReceived',
      },
      {
        label: 'Accumulative',
        id: 'accumulative',
      },
      {
        label: 'Accumulative Received',
        id: 'accumulativeReceived',
      },
    ];

    let allHeaders = [...headers, ...depositHeaders, ...totalHeaders];

    let excelWidths = depositColumns.map((unique: any) => {
      return {
        [normalToCamel(unique)]: 15,
      };
    });

    let pdfWidths = depositColumns.map((unique: any) => {
      return {
        [normalToCamel(unique)]: 200,
      };
    });

    let mergeObjects = pdfWidths.reduce(function (result, currentObject) {
      for (var key in currentObject) {
        if (currentObject.hasOwnProperty(key)) {
          result[key] = currentObject[key];
        }
      }
      return result;
    }, {});

    let mergeExcelObjects = excelWidths.reduce(function (result, currentObject) {
      for (var key in currentObject) {
        if (currentObject.hasOwnProperty(key)) {
          result[key] = currentObject[key];
        }
      }
      return result;
    }, {});

    let exWidths = {
      date: 15,
      monthlyTotal: 15,
      monthlyReceived: 15,
      accumulative: 15,
      accumulativeReceived: 15,
    };

    let fixedWidths = {
      date: 200,
      monthlyTotal: 200,
      monthlyReceived: 200,
      accumulative: 200,
      accumulativeReceived: 200,
    };

    let allExcelWidths = {
      ...mergeExcelObjects,
      ...exWidths,
    };

    let allPdfWidths = {
      ...mergeObjects,
      ...fixedWidths,
    };

    let sheetTitle = `${project.name} - ${capitalizeFirstLetter('Monthly Deposit Summary')}`;

    if (type === 'excel') {
      downloadExcel([unitColumns], [allHeaders], [], [[allExcelWidths]], [sheetTitle], sheetTitle);
    } else {
      downloadPdf([unitColumns], [allHeaders], [], [allPdfWidths], [sheetTitle], sheetTitle);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <FlexBetween>
        <Typography variant={'h5'} gutterBottom>
          Monthly Deposits
        </Typography>
        <Button variant="contained" color="success" onClick={() => downloadDepositTracker()}>
          {reportLoading ? <CircularProgress size={24} color="secondary" /> : 'Download Deposit Tracker'}
        </Button>
      </FlexBetween>
      <Box sx={{ mb: 2 }}>
        The amount shown in green indicates the total amount that has been cleared. The deposit amounts will only be applicable to{' '}
        <strong>firm</strong> deals
      </Box>
      <StandardTable columns={columns} data={deposits} loading={loading} download={download} />
      <GlobalModal>
        <Typography variant={'h5'} gutterBottom>
          {title}
        </Typography>
        <StandardTable columns={unitColumns} data={suites} loading={suiteLoading} />
      </GlobalModal>
    </Box>
  );
};

const DEPOSITS = gql`
  query getDepositSummary($project: MongoID!) {
    getDepositSummary(project: $project) {
      _id {
        month
        year
        deposit
      }
      total
      receivedTotal
    }
  }
`;

const DEPOSITREPORT = gql`
  query getDepositReport($project: MongoID!) {
    getDepositReport(project: $project)
  }
`;

const DEPOSITUNITS = gql`
  query getDepositUnits($project: MongoID!, $name: String!, $date: Date!) {
    getDepositUnits(project: $project, name: $name, date: $date) {
      _id
      unit {
        suite
        _id
      }
      deposit {
        name
        dueDate
        chequeType
        amount
      }
    }
  }
`;

export default DailyDeposits;
